import {urlState} from 'app/client/models/gristUrlState';
import {colors, mediaMedium, theme} from 'app/client/ui2018/cssVars';
import {cssLink} from 'app/client/ui2018/links';
import * as css from 'app/client/ui/LoginPagesCss';
import {buildRegistrationForm, buildVerifyEmailForm} from 'app/client/ui/SignupForms';
import {LoginAPI} from 'app/common/LoginAPI';
import {Disposable, dom, makeTestId, Observable, styled} from 'grainjs';

const testId = makeTestId('test-sp-');

export class SignupPage extends Disposable {
  private _currentStep: Observable<'register' | 'verify-email'> = Observable.create(this, 'register');
  private _email = Observable.create(this, '');

  constructor(private _loginAPI: LoginAPI) {
    super();
  }

  public buildDom() {
    return css.pageContainer(
      cssSignupPage(
        cssSidebar(
          cssSidebarHeading('The world deserves a better tool than spreadsheets'),
          cssFeatureList(
            cssIconAndText(cssUnify(), cssFeatureText('Unify your data')),
            cssIconAndText(cssStreamline(), cssFeatureText('Streamline your data')),
            cssIconAndText(cssVisualize(), cssFeatureText('Visualize your data')),
          ),
          cssGetStarted(
            cssGetStartedImg({src: 'img/get-started.png'}),
          ),
          testId('sidebar'),
        ),
        this._buildSignupForms(),
      ),
    );
  }

  private _buildSignupForms() {
    return cssSignupFormWrap(cssSignupForm(
      dom.domComputed(this._currentStep, currentStep => {
        switch (currentStep) {
          case 'register': {
            return dom.create(buildRegistrationForm, {
              loginAPI: this._loginAPI,
              email: this._email,
              onSuccess: () => this._currentStep.set('verify-email'),
            });
          }
          case 'verify-email': {
            return dom.create(buildVerifyEmailForm, {
              loginAPI: this._loginAPI,
              email: this._email,
              headingText: 'Almost there!',
              footer: () => css.text(
                'Already have an account? ',
                cssLink('Sign in',
                  urlState().setLinkUrl({login: 'login'}),
                  testId('sign-in'),
                ),
              ),
            });
          }
        }
      })
    ));
  }
}

const cssSignupPage = styled('div', `
  display: flex;
  min-height: 100%;
  background-color: ${theme.loginPageBg};
`);

const cssSidebar = styled('div', `
  width: 460px;
  background-color: ${colors.lightGreen};
  color: ${colors.light};
  background-image:
    linear-gradient(to bottom, rgb(41, 185, 131) 32px, transparent 32px),
    linear-gradient(to right, rgb(41, 185, 131) 32px, transparent 32px);
  background-size: 240px 120px;
  background-position: 0 0, 40%;
  display: flex;
  flex-direction: column;

  @media ${mediaMedium} {
    & {
      display: none;
    }
  }
`);

const cssFeatureList = styled('div', `
  display: flex;
  flex-direction: column;
  margin: 32px 64px 32px 64px;
  gap: 16px;
`);

const cssIconAndText = styled('div', `
  display: flex;
  align-items: center;
  gap: 16px;
`);

const cssGetStarted = styled('div', `
  margin: auto -77px 0 37px;
  width: 500px;
  height: 350px;
  overflow: hidden;
`);

const cssGetStartedImg = styled('img', `
  display: block;
  width: 500px;
  height: auto;
`);

const cssSidebarHeading = styled('div', `
  line-height: 32px;
  margin: 76px 64px 0px 64px;
  font-size: 24px;
  font-weight: 500;
`);

const cssSignupFormWrap = styled('div', `
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  padding: 0px 80px;

  @media ${mediaMedium} {
    & {
      padding: 0px;
    }
  }
`);

const cssSignupForm = styled('div', `
  max-width: 464px;
  width: 100%;
  margin: 60px 20px 33px 20px;

  @media ${mediaMedium} {
    & {
      margin-top: 25px;
    }
  }
`);

const cssImage = styled(css.image, `
  width: 40px;
  height: 40px;
`);

const cssUnify = styled(cssImage, `
  background-image: var(--icon-LoginUnify);
`);

const cssStreamline = styled(cssImage, `
  background-image: var(--icon-LoginStreamline);
`);

const cssVisualize = styled(cssImage, `
  background-image: var(--icon-LoginVisualize);
`);

const cssFeatureText = styled('div', `
  font-weight: 400;
  line-height: 24px;
  font-size: 18px;
`);
